import { Input } from "@/components/ui/input";
import {
	InputSideLabel,
	InputSideLabelPopover,
} from "@/components/ui/InputSideLabel";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import type { ParameterInputProps } from "./types";
import { getInputClass } from "./utils";

export const MaxAgeInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"max-age">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[120px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.max-age")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.max-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.max-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MinAgeInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"min-age">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[120px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.min-age")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.min-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.min-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const StartAgeInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"start-age">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-28 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.s")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.start-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.start-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const CompensationTaperStartAgeInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"compensation-taper-start-age">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-28 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.s")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.compensation-taper-start-age"
						)}
						error={parameter.error}
						content={t(
							"admin.insurances.parameters.content.compensation-taper-start-age"
						)}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const EndAgeInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"end-age">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-28 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.e")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.end-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.end-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MaxAgeChildSupplementInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"max-age-child-supplement">) => {
	const hasChildSupplement = insurance.parameters?.find(
		(p) => p.type === "child-supplement"
	)?.value;

	if (!hasChildSupplement) {
		return null;
	}

	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[120px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.max-age")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.max-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.max-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MinAgeChildSupplementInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"min-age-child-supplement">) => {
	const hasChildSupplement = insurance.parameters?.find(
		(p) => p.type === "child-supplement"
	)?.value;

	if (!hasChildSupplement) {
		return null;
	}

	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[120px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.min-age")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.min-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.min-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MaxAgeDependentsSupplementInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"max-age-dependents-supplement">) => {
	const hasDependentsSupplement = insurance.parameters?.find(
		(p) => p.type === "dependents-supplement"
	)?.value;

	if (!hasDependentsSupplement) {
		return null;
	}

	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[120px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.max-age")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.max-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.max-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MinAgeDependentsSupplementInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"min-age-dependents-supplement">) => {
	const hasDependentsSupplement = insurance.parameters?.find(
		(p) => p.type === "dependents-supplement"
	)?.value;

	if (!hasDependentsSupplement) {
		return null;
	}

	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[120px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.min-age")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.min-age")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.min-age")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MaxAgeChildInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"max-age-child">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-28 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						{t("admin.insurances.parameters.type-to-name.max-age-child")}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t("admin.insurances.parameters.type-to-name.max-age-child")}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.max-age-child")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};
